import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSupport } from '../hooks/useSupport';

function IconeEngrenagem() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3.2" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.09A1.7 1.7 0 0 0 8.9 19.3a1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.7 15a1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.7 8.9a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.7a1.7 1.7 0 0 0 1.03-1.56V3a2 2 0 1 1 4 0v.09A1.7 1.7 0 0 0 15 4.7a1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.3 9v.01a1.7 1.7 0 0 0 1.56 1.02H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1z" />
    </svg>
  );
}

/** Selo de verificado: círculo recortado com o visto dentro. */
function IconeVerificado() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill="currentColor"
        d="M12 1.6l2.4 1.9 3-.3 1 2.9 2.6 1.6-1 2.9 1 2.9-2.6 1.6-1 2.9-3-.3-2.4 1.9-2.4-1.9-3 .3-1-2.9L2 13.5l1-2.9-1-2.9 2.6-1.6 1-2.9 3 .3z"
      />
      <path
        fill="none"
        stroke="#0e1116"
        strokeWidth="2.1"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.4 12.2l2.5 2.5 4.7-4.9"
      />
    </svg>
  );
}

function IconeSair() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </svg>
  );
}

/**
 * Conta no canto: avatar, nome e uma engrenagem que abre o resto.
 *
 * Antes eram três botões soltos disputando espaço com a logo. Agrupar tudo
 * atrás de um ícone deixa a barra limpa e dá lugar para o menu crescer — o
 * admin tem opções que o cliente nem deveria ver.
 */
export function AccountMenu() {
  const { user, loading, loginUrl, logout, isAdmin } = useAuth();
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const atendimento = useSupport();

  // Clique fora e Escape fecham: menu que só fecha no próprio botão irrita.
  useEffect(() => {
    if (!aberto) return;
    const foraDaCaixa = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    const teclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAberto(false);
    };
    document.addEventListener('mousedown', foraDaCaixa);
    document.addEventListener('keydown', teclado);
    return () => {
      document.removeEventListener('mousedown', foraDaCaixa);
      document.removeEventListener('keydown', teclado);
    };
  }, [aberto]);

  if (loading) return null;

  if (!user) {
    return (
      <a className="btn primary small" href={loginUrl}>
        Entrar com a Steam
      </a>
    );
  }

  const ir = (rota: string) => {
    setAberto(false);
    navigate(rota);
  };

  return (
    <div className="conta" ref={caixa}>
      {user.avatarUrl && <img className="avatar" src={user.avatarUrl} alt="" />}
      <div className="conta-info">
        <span className="conta-nome">
          <b>{user.personaName ?? user.steamId}</b>
          {/* O selo diz o mesmo que a frase dizia, sem ocupar uma segunda
              linha. O título mantém a informação para leitor de tela. */}
          {!user.needsOnboarding && (
            <span className="selo-verificado" title="Conta verificada" role="img" aria-label="Conta verificada">
              <IconeVerificado />
            </span>
          )}
        </span>
        {user.needsOnboarding && (
          <Link className="conta-alerta" to="/cadastro">
            completar cadastro
          </Link>
        )}
      </div>

      <button
        className={`conta-engrenagem ${aberto ? 'on' : ''}`}
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        aria-haspopup="menu"
        aria-label="Opções da conta"
        title="Opções da conta"
      >
        <IconeEngrenagem />
      </button>

      {aberto && (
        <div className="conta-menu" role="menu">
          <button role="menuitem" onClick={() => ir('/favoritos')}>
            <span aria-hidden>★</span> Favoritos
          </button>
          <button role="menuitem" onClick={() => ir('/pedidos')}>
            <span aria-hidden>📦</span> Meus pedidos
          </button>
          {/* Dúvidas não é uma página: abre a janela de atendimento, que
              fica no canto e acompanha o cliente em qualquer tela. */}
          <button
            role="menuitem"
            onClick={() => {
              setAberto(false);
              atendimento.abrir();
            }}
          >
            <span aria-hidden>💬</span> Dúvidas
          </button>
          <button role="menuitem" onClick={() => ir('/feedbacks')}>
            <span aria-hidden>⭐</span> Feedbacks dos clientes
          </button>

          {isAdmin && (
            <>
              <div className="conta-menu-sep">Administração</div>
              <button role="menuitem" onClick={() => ir('/admin')}>
                <span aria-hidden>⚙️</span> Painel
              </button>
              {/* Direto na aba de pedidos: é a tela que a operação abre o dia todo. */}
              <button role="menuitem" onClick={() => ir('/admin?aba=pedidos')}>
                <span aria-hidden>🧾</span> Gestão de pedidos
              </button>
            </>
          )}

          <button
            role="menuitem"
            className="conta-sair"
            onClick={() => {
              setAberto(false);
              void logout();
            }}
          >
            <IconeSair /> Sair da conta
          </button>
        </div>
      )}
    </div>
  );
}
